import React, {useEffect, useState} from 'react';
import {Chip, Divider, IconButton, Stack, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';

import {ManageClientService} from '@store/clients/services';

import {AddWhite} from '@icons/index';
import ManagePriceModal
  from '@screens/admin/clients/details/contractedServices/manageService/components/ManagePrice.modal';
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineOppositeContent,
  timelineOppositeContentClasses,
  TimelineSeparator
} from '@mui/lab';
import dayjs from 'dayjs';
import {NumberExt} from '@utils/extensions/number.extension';
import {TrendingFlat} from '@mui/icons-material';
import {ManagePrice} from '@store/common/price.model';
import {useSearchParams} from 'react-router-dom';

interface PricesFormProps {
  isCreation?: boolean;
  service: ManageClientService;
  updateService: (data: ManageClientService) => void;
  handleNewPrice: (price: ManagePrice) => void;
}

const PricesForm = (props: PricesFormProps) => {
  const {service, handleNewPrice} = props;

  const [searchParams] = useSearchParams();

  const [priceCount, setPriceCount] = useState(3);

  const {t} = useTranslation();

  const [showPrice, setShowPrice] = useState<number | boolean>(false);

  const pricePeriods = service.pricePeriods?.sort((p1, p2) => new Date(p2.fromDate).getTime() - new Date(p1.fromDate).getTime()).slice(0, priceCount);

  const newBuyPrice = searchParams.get('newBuyPrice');

  useEffect(() => {
    if (newBuyPrice != null) {
      setShowPrice(true);
    }
  }, [newBuyPrice]);

  return (
    <>
      <Stack py="20px" spacing={2}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography fontSize={16} fontWeight="700">
            {t('clients.details.contractedServices.manage.price')}
          </Typography>
          <IconButton aria-label="small-square-text" centerRipple={false} onClick={() => setShowPrice(true)}>
            <AddWhite/>
          </IconButton>
        </Stack>
        <Stack spacing={4}>
          <Timeline sx={{
            [`& .${timelineOppositeContentClasses.root}`]: {
              flex: 0.3,
              paddingLeft: 0
            },
            padding: 0
          }}>
          {pricePeriods?.map((period) => (
              <TimelineItem key={period.id}>
                <TimelineOppositeContent>
                  <Typography fontSize={16} fontWeight="700">
                    {dayjs(period.fromDate).format('DD.MM.YYYY')}
                  </Typography>
                </TimelineOppositeContent>
                <TimelineSeparator>
                  <TimelineDot/>
                  <TimelineConnector/>
                </TimelineSeparator>
                {period.sellPrices && period.sellPrices.length > 0 && <TimelineContent>
                    <Stack direction="column">
                        <Stack direction="row" spacing={1} alignItems="center"><TrendingFlat color="success"
                                                                                             sx={{rotate: '180deg'}}/><Typography>{NumberExt.round(period.sellPrices[0].unitPriceInCents / 100, 2)} CHF</Typography></Stack>
                        <Stack direction="row" spacing={1} alignItems="center"><TrendingFlat
                            color="warning"/><Typography>{NumberExt.round(period.sellPrices[0].unitCostInCents / 100, 2)} CHF</Typography></Stack>
                    </Stack>
                </TimelineContent>}
              </TimelineItem>
            ))}
            {service.pricePeriods && priceCount < service.pricePeriods?.length && (
              <TimelineItem>
                <TimelineOppositeContent>

                </TimelineOppositeContent>
                <TimelineSeparator sx={{marginLeft: -7, marginTop: 1}}>
                  <Chip onClick={() => setPriceCount(priceCount + 3)} sx={{fontSize: 10}} label="..."/>
                </TimelineSeparator>
                <TimelineContent>
                </TimelineContent>
              </TimelineItem>
            )}
          </Timeline>
        </Stack>
      </Stack>
      <Divider variant="fullWidth" orientation="horizontal"/>
      {showPrice !== false && (
        <ManagePriceModal
          handleClose={() => setShowPrice(false)}
          price={typeof showPrice === 'boolean' ? undefined : service.sellPrices?.[showPrice]}
          newPrice={newBuyPrice != null ? parseInt(newBuyPrice, 10) : undefined}
          unit={service.supplierService?.kind?.execUnitName1}
          minDate={pricePeriods?.[0]?.fromDate}
          outTaxRule={service?.supplierService?.supplierOrganisationUnit?.supplierOrganisation?.outTaxRule}
          currentPrice={{
            unitCost: (service.pricePeriods?.[0]?.sellPrices?.[0]?.unitCostInCents || 0) / 100,
            unitPrice: (service.pricePeriods?.[0]?.sellPrices?.[0]?.unitPriceInCents || 0) / 100,
          }}
          handleSaved={handleNewPrice}/>
      )}
    </>
  );
};

export default PricesForm;
